import { type UseInfiniteQueryOptions } from "@tanstack/react-query";
import { type AxiosError } from "axios";

import { type GetPlacementApplicationsResponse } from "../types/fetchPlacementApplications.schema";
import { PlacementApplicationStatus } from "../types/fetchPlacements.schema";
import {
  type FetchPlacementApplicationsParams,
  useFetchPlacementApplications,
} from "./useFetchPlacementApplications";

/**
 * Default page size for easy scheduling placement applications.
 * Larger than default to enable client-side counting without additional requests.
 */
const DEFAULT_PAGE_SIZE = 200;

interface UseGetEasySchedulingPlacementApplicationsParams extends Omit<
  FetchPlacementApplicationsParams,
  "filter"
> {
  additionalFilters?: FetchPlacementApplicationsParams["filter"];
}

/**
 * Fetch placement applications with easy scheduling enabled.
 *
 * Since the /placement-applications endpoint does not support the applicationMethod filter,
 * this hook fetches all accepted applications. The consumer must filter client-side
 * for applicationMethod === "easy_scheduling".
 */
export function useGetEasySchedulingPlacementApplications(
  params: UseGetEasySchedulingPlacementApplicationsParams,
  options: UseInfiniteQueryOptions<GetPlacementApplicationsResponse, AxiosError> = {}
) {
  const { workerId, additionalFilters, sort, pageSize } = params;

  return useFetchPlacementApplications(
    {
      workerId,
      sort,
      pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
      filter: {
        ...additionalFilters,
        status: PlacementApplicationStatus.ACCEPTED,
      },
    },
    options
  );
}
